export const MAX_EXTENDER_UPLOAD_BYTES = 100 * 1024 * 1024;
export const EXTENDER_VIDEO_ACCEPT =
  'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

const SUPPORTED_VIDEO_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

export interface VideoFileMetadata {
  duration: number;
  height: number;
  width: number;
}

export function validateExtenderVideoFile(file: File) {
  if (
    !SUPPORTED_VIDEO_TYPES.has(file.type) &&
    !/\.(mp4|webm|mov)$/i.test(file.name)
  ) {
    throw new Error('Choose an MP4, WebM, or QuickTime video.');
  }
  if (file.size > MAX_EXTENDER_UPLOAD_BYTES) {
    throw new Error('Videos must be smaller than 100MB.');
  }
}

export async function readVideoFileMetadata(
  file: File
): Promise<VideoFileMetadata> {
  validateExtenderVideoFile(file);
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    await waitForVideoEvent(video, 'loadedmetadata');
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      throw new Error('The video duration could not be read.');
    }
    return {
      duration: video.duration,
      height: video.videoHeight,
      width: video.videoWidth,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function extractVideoLastFrame(file: File): Promise<File> {
  validateExtenderVideoFile(file);
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    await waitForVideoEvent(video, 'loadedmetadata');
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      throw new Error('The video duration could not be read.');
    }
    video.currentTime = Math.max(0, video.duration - 0.08);
    await waitForVideoEvent(video, 'seeked');

    const scale = Math.min(
      1,
      1920 / Math.max(video.videoWidth, video.videoHeight)
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const context = canvas.getContext('2d');
    if (!context)
      throw new Error('The browser could not read the final frame.');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToBlob(canvas);
    return new File([blob], 'video-extender-last-frame.jpg', {
      type: 'image/jpeg',
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function waitForVideoEvent(video: HTMLVideoElement, eventName: string) {
  return new Promise<void>((resolve, reject) => {
    const onResolve = () => {
      cleanup();
      resolve();
    };
    const onReject = () => {
      cleanup();
      reject(new Error('The browser could not read this video.'));
    };
    const cleanup = () => {
      video.removeEventListener(eventName, onResolve);
      video.removeEventListener('error', onReject);
    };
    video.addEventListener(eventName, onResolve, { once: true });
    video.addEventListener('error', onReject, { once: true });
  });
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The final frame could not be prepared.')),
      'image/jpeg',
      0.9
    );
  });
}
